import { useState, useEffect, useRef } from 'react';
import { FlowNode } from '../../types';

interface Props {
  node: FlowNode;
  onUpdate: (updated: FlowNode) => void;
}

interface MappingRow {
  rowId: string;
  key: string;
  value: string;
}

function toRows(raw: unknown, idRef: { current: number }): MappingRow[] {
  if (typeof raw !== 'object' || raw === null) return [];
  return Object.entries(raw as Record<string, unknown>).map(([key, value]) => {
    idRef.current += 1;
    return {
      rowId: `row-${idRef.current}`,
      key,
      value: typeof value === 'string' ? value : JSON.stringify(value),
    };
  });
}

export function LookupStepConfig({ node, onUpdate }: Props) {
  const config = (node.config ?? {}) as Record<string, unknown>;
  const idCounter = useRef(0);

  const [alias, setAlias] = useState((config.alias as string) ?? '');
  const [input, setInput] = useState((config.input as string) ?? '');
  const [defaultValue, setDefaultValue] = useState((config.default as string) ?? '');
  const [rows, setRows] = useState<MappingRow[]>(toRows(config.table, idCounter));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setAlias((config.alias as string) ?? '');
    setInput((config.input as string) ?? '');
    setDefaultValue((config.default as string) ?? '');
    setRows(toRows(config.table, idCounter));
  }, [node.id]);

  useEffect(() => {
    const { alias: _a, input: _i, table: _t, default: _d, ...rest } = config;
    const table: Record<string, string> = {};
    rows.forEach(r => {
      if (r.key.trim() && r.value.trim()) table[r.key.trim()] = r.value.trim();
    });
    const next = {
      ...rest,
      ...(input.trim() ? { input: input.trim() } : {}),
      ...(Object.keys(table).length > 0 ? { table } : {}),
      ...(defaultValue.trim() ? { default: defaultValue.trim() } : {}),
      ...(alias.trim() ? { alias: alias.trim() } : {}),
    };
    if (JSON.stringify(next) === JSON.stringify(node.config ?? {})) return;
    const t = setTimeout(() => onUpdate({ ...node, config: next }), 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alias, input, defaultValue, rows]);

  function addRow() {
    idCounter.current += 1;
    setRows([...rows, { rowId: `row-${idCounter.current}`, key: '', value: '' }]);
  }

  function removeRow(rowId: string) {
    setRows(rows.filter(r => r.rowId !== rowId));
  }

  function updateRow(rowId: string, field: 'key' | 'value', val: string) {
    setRows(rows.map(r => (r.rowId === rowId ? { ...r, [field]: val } : r)));
  }

  return (
    <div className="flex flex-col gap-3 flex-1 overflow-y-auto">

      {/* Alias */}
      <div className="flex flex-col gap-1">
        <label className="text-xs text-muted-foreground font-medium">Step alias</label>
        <input
          type="text"
          className="border border-input rounded p-1.5 text-xs font-mono bg-background"
          placeholder="e.g. type"
          value={alias}
          onChange={e => setAlias(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
        />
        {alias && (
          <p className="text-xs text-muted-foreground">
            Mapped value via <span className="font-mono text-tone-blue">{`{{${alias}.value}}`}</span>
          </p>
        )}
      </div>

      {/* Input expression */}
      <div className="flex flex-col gap-1">
        <label className="text-xs text-muted-foreground font-medium">Input expression</label>
        <input
          type="text"
          className="border border-input rounded p-1.5 text-xs font-mono bg-background"
          placeholder="{{absence.timeOffTypeName}}"
          value={input}
          onChange={e => setInput(e.target.value)}
        />
        <p className="text-xs text-muted-foreground">
          Resolved and compared (case-insensitive) against each mapping's candidate list. First match wins.
        </p>
      </div>

      {/* Mapping table */}
      <div className="flex flex-col gap-2">
        <label className="text-xs text-muted-foreground font-medium">Mappings (output value ← candidates)</label>
        {rows.map(r => (
          <div key={r.rowId} className="flex gap-1 items-center">
            <input
              type="text"
              className="w-1/3 border border-input rounded p-1.5 text-xs font-mono bg-background"
              placeholder="Vacation"
              value={r.key}
              onChange={e => updateRow(r.rowId, 'key', e.target.value)}
            />
            <input
              type="text"
              className="flex-1 border border-input rounded p-1.5 text-xs font-mono bg-background"
              placeholder='{{cfg.settings.vacation}} or ["A","B"]'
              value={r.value}
              onChange={e => updateRow(r.rowId, 'value', e.target.value)}
            />
            <button
              onClick={() => removeRow(r.rowId)}
              className="text-destructive hover:text-destructive/80 text-xs px-2"
              title="Remove mapping"
            >
              ✕
            </button>
          </div>
        ))}
        <button
          onClick={addRow}
          className="w-full border border-input text-xs text-muted-foreground rounded py-1 hover:bg-muted"
        >
          + Add mapping
        </button>
        <p className="text-xs text-muted-foreground">
          The value column is a template that resolves to a JSON array (e.g. a per-customer config) or an
          inline array/string of candidate inputs. The output value is the column on the left.
        </p>
      </div>

      {/* Default */}
      <div className="flex flex-col gap-1">
        <label className="text-xs text-muted-foreground font-medium">Default (no match)</label>
        <input
          type="text"
          className="border border-input rounded p-1.5 text-xs font-mono bg-background"
          placeholder="e.g. SpecialLeave"
          value={defaultValue}
          onChange={e => setDefaultValue(e.target.value)}
        />
      </div>
    </div>
  );
}
